/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  styled,
  SvgIconTypeMap,
  OutlinedInput
} from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { TSocialMedia } from 'src/models/general';
import { IObject } from 'src/models/objectManagement';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TwitterIcon from '@mui/icons-material/X';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import useObjectManagement from 'src/services/useObjectManagement';
import { useAppSelector } from 'src/app/hooks';
import { IObjectInput, TObjectType } from 'src/models/objectManagement';
import useOnboarding from 'src/services/useOnboarding';
import Confirmation from '../Confirmation';
import GetSocialMediaIcon from '../GetSocialMediaIcon';
import TiktokIcon from '../TikTokIcon/TikTokIcon';
import { useApolloClient } from '@apollo/client';
import {
  GET_OBJECTS_WITH_ENTITY,
  GET_OBJECTS_WITH_STATUS
} from 'src/services/graphql/objectManagement/query';
import useUsers from 'src/services/useUsers';
import { useLocation } from 'react-router';
import { ECOMMERCE_GET_OBJECTS } from 'src/services/graphql/ecommerce/query';
import { isValidURL } from 'src/utils/isValidURL';
import { getSocmedFromDomain } from 'src/utils/getSocmedFromDomain';

interface ISocialMedia {
  id: TSocialMedia;
  label: string;
  icon:
    | (OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & {
        muiName: string;
      })
    | any;
}

interface Props {
  objectType?: TObjectType;
  width?: number;
  onClose?: () => void;
}

const CustomButton = styled(Button)(
  ({ theme }) => `
    border: unset;
      border-radius: 6px 0 0 6px;
      background-color: ${theme.palette.grey[200]};
      border: 1px solid ${theme.palette.divider};
      border-color: ${theme.palette.common.black}
      color: ${theme.palette.primary.main};
      &:hover{
        background-color: ${theme.palette.grey[300]};
      }

    `
);

const ListItemButtonCustom = styled(ListItemButton)(
  ({ theme }) => `
      &:hover {
        background-color: ${theme.palette.primary.light}
      }
    `
);

const listOptionSocialMedia: Array<ISocialMedia> = [
  {
    id: 'twitter',
    label: 'X',
    icon: TwitterIcon
  },
  {
    id: 'facebook',
    label: 'Facebook',
    icon: FacebookIcon
  },
  {
    id: 'instagram',
    label: 'Instagram',
    icon: InstagramIcon
  },
  {
    id: 'youtube',
    label: 'Youtube',
    icon: YouTubeIcon
  },
  {
    id: 'tiktok',
    label: 'Tiktok',
    icon: TiktokIcon
  }
];

const InputPostLink: FC<Props> = ({ objectType, onClose }) => {
  const client = useApolloClient();
  const objectListWithStatus = useAppSelector(
    (state) => state.objectManagement.objectListWithStatus
  );
  const { pathname } = useLocation();
  const title = pathname.split('/')[2];
  const isEcommerce = title === 'ecommerce';

  const [socialMedia, setSocialMedia] = useState<TSocialMedia>(
    isEcommerce ? 'tokopedia' : 'twitter'
  );
  const [openAddConfirmation, setOpenAddConfirmation] =
    useState<boolean>(false);
  const [content, setContent] = useState<string>('');

  const objectListPostLink = useMemo((): Array<IObject> => {
    return objectListWithStatus.filter(
      (object) => object.streamType === 'post_link'
    );
  }, [objectListWithStatus]);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleChangeSocialMedia = (socialMediaId: TSocialMedia) => {
    setSocialMedia(socialMediaId);
    handleClose();
  };

  const project = useAppSelector((state) => state.users.project);
  const { createObjects, responseCreateObjects } = useOnboarding();
  const { getObjectList } = useObjectManagement();
  const { getProjectDetailResult } = useUsers();

  const handleSubmit = (objectInput: IObjectInput) => {
    const { streamType, ...rest } = objectInput;
    const payload = {
      projectId: project.id,
      streamType: objectType,
      objectInput: [rest]
    };
    createObjects({
      variables: payload
    })
      .then(() => {
        getObjectList(project.id);
        getProjectDetailResult(project.id);
        client.refetchQueries({
          include: [GET_OBJECTS_WITH_ENTITY, GET_OBJECTS_WITH_STATUS]
        });
      })
      .then(() => {
        onClose();
      });
  };

  const handleAdd = () => {
    const tempObjectInput: IObjectInput = {
      content: content,
      displayPic: '',
      excludes: [],
      extraContent: '',
      includes: [],
      isHashtag: false,
      languages: '',
      objectName: content,
      socialMedia: socialMedia,
      streamType: objectType
    };
    handleSubmit(tempObjectInput);
  };

  const isObjectAlreadyIncluded = () => {
    // find if post link is included in object list
    return objectListPostLink.some(
      (object) =>
        object.objectName === content &&
        object.content === content &&
        object.socialMedia === socialMedia
    );
  };

  const handleChangeInputUrl = (url: string) => {
    setContent(url);
    if (isValidURL(url) && getSocmedFromDomain(url)) {
      setSocialMedia(getSocmedFromDomain(url));
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row'
        }}
      >
        <CustomButton
          variant="contained"
          onClick={!content ? (event) => handleClick(event) : undefined}
        >
          <GetSocialMediaIcon socialMedia={socialMedia} color="primary" />
          <ArrowDropDownIcon fontSize="small" color="primary" sx={{ ml: 1 }} />
        </CustomButton>
        <OutlinedInput
          fullWidth
          placeholder={'Enter your post URL here'}
          onChange={(event) => handleChangeInputUrl(event.target.value)}
          value={content}
          sx={{
            borderRadius: 0
          }}
        />
        <Button
          variant="contained"
          sx={{ borderRadius: '0px' }}
          disabled={!content || responseCreateObjects.loading}
          onClick={() =>
            isObjectAlreadyIncluded()
              ? setOpenAddConfirmation(true)
              : handleAdd()
          }
        >
          {responseCreateObjects.loading ? 'Saving...' : 'Save'}
        </Button>
      </Box>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <List>
          {listOptionSocialMedia.map((socialMedia) => (
            <ListItem disablePadding key={socialMedia.id}>
              <ListItemButtonCustom
                onClick={() => handleChangeSocialMedia(socialMedia.id)}
              >
                <ListItemIcon>{<socialMedia.icon />}</ListItemIcon>
                <ListItemText primary={socialMedia.label} />
              </ListItemButtonCustom>
            </ListItem>
          ))}
        </List>
      </Popover>
      <Confirmation
        title={'Add this object?'}
        labelButton={'Add'}
        message={`The post link you want to add has already been added.`}
        open={openAddConfirmation}
        onClose={() => setOpenAddConfirmation(false)}
        onOk={() => {
          handleAdd();
        }}
      />
    </>
  );
};

export default InputPostLink;
